import {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import "./SearchGlobalField.scss";
import Select, {
  ActionMeta,
  InputActionMeta,
  OnChangeValue,
} from "react-select";
import {
  CaretDownIcon,
  Checkbox,
  ContentArea,
  MagnifyingGlassIcon,
} from "/App.Components";
import { DesignTheme } from "/App.GeneratedEnums";
import { createSpreadableBem } from "/Helpers/createBem";
import useDebounce from "/Helpers/Hooks/useDebounce";
import { ReactSelectItem } from "/Helpers/ReactSelectItem";
import { ColorTheme } from "/Models/Models/ColorTheme";
import { ContentAreaProps } from "../../../App.GeneratedTypes";
import SearchGlobalFieldOption from "./SearchGlobalFieldOption";

export interface SearchGlobalFieldProps {
  doSearch: (searchTerm: string) => void;
  doAutocomplete: (text: string) => void;
  id: string;
  tabIndex?: number;
  searchTerm?: string;
  setSearchTerm: (searchTerm: string) => void;
  autocompleteItems?: ReactSelectItem[];
  setAutocompleteItems: (autocompleteItems: ReactSelectItem[]) => void;
  searchButtonAriaLabel: string;
  searchPlaceholder: string;
  noBottomPadding?: boolean;
  noTopPadding?: boolean;
  searchTips?: ContentAreaProps;
  searchTipsLabel?: string;
  fuzzySearchLabel?: string;
  fuzzySearch?: boolean;
  setFuzzySearch?: Dispatch<SetStateAction<boolean>>;
  theme?: DesignTheme;
  size?: "medium";
}

const SearchGlobalField = ({
  tabIndex,
  autocompleteItems,
  setAutocompleteItems,
  doAutocomplete: onAutocomplete,
  doSearch,
  setSearchTerm,
  searchTerm,
  searchButtonAriaLabel,
  searchPlaceholder,
  noBottomPadding,
  noTopPadding,
  id,
  fuzzySearch,
  fuzzySearchLabel,
  setFuzzySearch,
  searchTips,
  searchTipsLabel,
  theme,
  size,
}: SearchGlobalFieldProps) => {
  const [selectedOption, setSelectedOption] = useState<ReactSelectItem>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [initialized, setInitialized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [tipsExpanded, setTipsExpanded] = useState(false);
  const [suggestionPickedWithArrows, setSuggestionPickedWithArrows] =
    useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      setInitialized(true);
      return;
    }
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    setIsMobile(window.innerWidth < 1024);
    setInitialized(true);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const debouncedAutocomplete = useDebounce(
    typeof onAutocomplete !== "undefined"
      ? onAutocomplete
      : (_text: string) => {},
    300
  );

  const emptyOption = () => ({ label: "ban", value: 10 });

  //if user actually selects option
  const onChange = (
    newValue: OnChangeValue<ReactSelectItem, false>,
    actionMeta: ActionMeta<ReactSelectItem>
  ) => {
    switch (actionMeta.action) {
      case "select-option": {
        // Passed on directly: the parent's searchTerm state still holds what was typed until the next render.
        const selectedTerm = (newValue as ReactSelectItem).label;
        setSearchTerm(selectedTerm);
        setSelectedOption(newValue as ReactSelectItem);
        doSearch(selectedTerm);
        setIsOpen(false);
        break;
      }
      case "clear":
        setSelectedOption(emptyOption);
        break;
    }
  };

  //when user types
  const onInputChange = (newValue: string, actionMeta: InputActionMeta) => {
    switch (actionMeta.action) {
      case "set-value":
        setSearchTerm(newValue);
        debouncedAutocomplete(newValue);
        break;
      case "input-change":
        setSearchTerm(newValue);
        setSuggestionPickedWithArrows(false);
        if (newValue) {
          debouncedAutocomplete(newValue);
        } else {
          // Cleared right away, not after the debounce: typing again within it would show the old suggestions.
          debouncedAutocomplete.cancel();
          setAutocompleteItems([]);
          onAutocomplete?.("");
        }
        break;
      case "input-blur":
        break;
    }
  };

  // react-select highlights the first suggestion, so Enter would search for that instead of what was typed.
  // Only let Enter pick a suggestion the user moved to with the arrow keys.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      setSuggestionPickedWithArrows(true);
    } else if (e.key === "Enter" && !suggestionPickedWithArrows) {
      handleSubmit(e);
      setIsOpen(false);
    }
  };

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setSelectedOption(emptyOption);
    doSearch(searchTerm ?? "");
  };

  const atLeastOneMatch = (): boolean =>
    autocompleteItems && searchTerm
      ? autocompleteItems.some((x) =>
          x.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : false;

  const bem = createSpreadableBem("search-global-field-norad");

  const isMedium = size === "medium";
  const fontSize =
    isMedium || theme === DesignTheme.Insight
      ? isMobile
        ? "1rem"
        : "1.25rem"
      : isMobile
        ? "1.25rem"
        : "1.5rem";
  return (
    <form
      onSubmit={handleSubmit}
      {...bem("", {
        "--no-bottom-padding": noBottomPadding,
        "--no-top-padding": noTopPadding,
        "--medium": isMedium,
      })}
    >
      <div>
        <div
          {...bem("__input-container", {
            "--half-round": atLeastOneMatch() && isOpen,
            "--theme-insight": theme === DesignTheme.Insight,
          })}
        >
          {initialized && (
            <>
              <label htmlFor={id}>
                <span {...bem("__sr-label")}>{searchButtonAriaLabel}</span>
              </label>
              <Select
                inputId={id}
                isDisabled={tabIndex === -1}
                options={autocompleteItems}
                value={selectedOption}
                inputValue={searchTerm}
                menuIsOpen={atLeastOneMatch() && isOpen}
                onInputChange={onInputChange}
                onKeyDown={onKeyDown}
                onMenuOpen={() => setIsOpen(true)}
                onMenuClose={() => {
                  setIsOpen(false);
                  setSuggestionPickedWithArrows(false);
                }}
                closeMenuOnSelect={false}
                openMenuOnFocus={false}
                openMenuOnClick={false}
                placeholder={initialized ? searchPlaceholder : ""}
                onChange={onChange}
                tabSelectsValue={false}
                controlShouldRenderValue={false}
                styles={{
                  placeholder: (baseStyles) => ({
                    ...baseStyles,
                    color: ColorTheme.textNeutral,
                    fontSize: fontSize,
                    fontFamily: "NoradSans",
                    fontWeight:
                      isMedium || theme === DesignTheme.Insight
                        ? 400
                        : baseStyles.fontWeight,
                    marginLeft:
                      isMedium || theme === DesignTheme.Insight
                        ? 0
                        : baseStyles.marginLeft,
                    lineHeight: isMedium
                      ? "1"
                      : theme === DesignTheme.Insight
                        ? "1.5"
                        : baseStyles.lineHeight,
                  }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    top: isMobile ? "calc(100% + 9px)" : "calc(100% + 7px)",
                    position: "absolute",
                    left: -1,
                    margin: "0px",
                    paddingTop: "24px",
                    paddingBottom: "19px",
                    border:
                      "1px solid " +
                      (theme === DesignTheme.Insight
                        ? ColorTheme.insightDarkBlue18
                        : ColorTheme.borderWeaker),
                    borderRadius: "none",
                    boxSizing: "content-box",
                    boxShadow: "none",
                  }),
                  menuList: (baseStyles) => ({
                    ...baseStyles,
                    padding: "unset",
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                    color:
                      theme === DesignTheme.Insight
                        ? ColorTheme.insightDarkBlue
                        : ColorTheme.darkGreen,
                    zIndex: 1,
                    fontFamily: "NoradSans",
                    fontSize: fontSize,
                    fontWeight:
                      isMedium || theme === DesignTheme.Insight ? 400 : 500,
                    lineHeight: isMedium
                      ? "1"
                      : theme === DesignTheme.Insight
                        ? "1.5"
                        : "1.25rem",
                    margin: isMedium ? 0 : baseStyles.margin,
                    padding: isMedium ? 0 : baseStyles.padding,
                    marginLeft:
                      isMedium || theme === DesignTheme.Insight ? 0 : undefined,
                    "&:hover": {
                      cursor: "text",
                    },
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                    width: "100%",
                    padding: isMedium
                      ? isMobile
                        ? "0 3rem 0 1.25rem"
                        : "0 3.5rem 0 2rem"
                      : theme === DesignTheme.Insight
                        ? "0 0 0 calc(1.25rem - 1px)"
                        : isMobile
                          ? "0 0 0 0.75rem"
                          : "1px 3.5rem 0 1.5rem",
                    position: "absolute",
                    left: 0,
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                    padding:
                      isMedium || theme === DesignTheme.Insight
                        ? "0"
                        : baseStyles.padding,
                  }),
                  control: (baseStyles) => ({
                    ...baseStyles,
                    border: "0px",
                    boxShadow: "none",
                    borderRadius: "3rem",
                    minHeight: isMedium ? 0 : baseStyles.minHeight,
                  }),
                  option: (baseStyles, state) => ({
                    ...baseStyles,
                    // The first suggestion starts out focused, but Enter only picks it after arrow keys (see onKeyDown).
                    backgroundColor:
                      state.isFocused && !suggestionPickedWithArrows
                        ? "transparent"
                        : baseStyles.backgroundColor,
                    "&:hover": {
                      backgroundColor: state.theme.colors.primary25,
                    },
                    fontFamily: "NoradSans",
                    fontSize: fontSize,
                    fontWeight: 400,
                    padding:
                      theme === DesignTheme.Insight
                        ? "0"
                        : isMobile
                          ? "2px 32px 3px"
                          : "1px 32px",
                  }),
                }}
                components={{
                  IndicatorSeparator: null,
                  DropdownIndicator: null,
                  Option: SearchGlobalFieldOption,
                }}
              />
            </>
          )}
          <button
            type="submit"
            {...bem("__icon", {
              "--theme-insight": theme === DesignTheme.Insight,
            })}
            tabIndex={tabIndex}
            aria-label={searchButtonAriaLabel}
          >
            <MagnifyingGlassIcon
              size={isMedium || theme === DesignTheme.Insight ? "20" : "24"}
              thickness="Reg"
            />
          </button>
        </div>
        {(searchTips || fuzzySearchLabel) && (
          <div
            {...bem("__fuzzy-and-tips-expander", {
              "--theme-insight": theme === DesignTheme.Insight,
            })}
          >
            {searchTips && searchTipsLabel && (
              <button
                {...bem("__tips-expander", {
                  "--theme-insight": theme === DesignTheme.Insight,
                })}
                onClick={(e) => {
                  e.preventDefault();
                  setTipsExpanded((old) => !old);
                }}
              >
                <div
                  {...bem("__tips-label", {
                    "--theme-insight": theme === DesignTheme.Insight,
                  })}
                >
                  {searchTipsLabel}
                </div>
                <div
                  {...bem("__icon-container", {
                    "--rotated": tipsExpanded,
                    "--theme-insight": theme === DesignTheme.Insight,
                  })}
                >
                  <CaretDownIcon
                    size="24"
                    thickness="Reg"
                    color={
                      theme === DesignTheme.Insight
                        ? ColorTheme.insightDarkBlue
                        : undefined
                    }
                  />
                </div>
              </button>
            )}
            {fuzzySearchLabel && setFuzzySearch && (
              <Checkbox
                label={fuzzySearchLabel}
                name="fuzzy-search"
                checked={fuzzySearch}
                onClick={() => setFuzzySearch((old) => !old)}
                theme={theme}
              />
            )}
          </div>
        )}
        {searchTips && (
          <div {...bem("__expanded-tips", { "--expanded": tipsExpanded })}>
            <ContentArea
              {...searchTips}
              richText={{
                theme:
                  theme === DesignTheme.Insight
                    ? "searchtips-insight"
                    : "searchtips",
              }}
            />
          </div>
        )}
      </div>
    </form>
  );
};

export default SearchGlobalField;
